import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from 'shared/components/confirmation-dialog/confirmation-dialog.component';
import { PartnerResourceService } from 'core/services/partner-resource.service';
import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelectChange } from '@angular/material/select';
import {
  GhostLoaderType,
  PartnerResourceTrackingType,
  PartnerResourceType,
} from 'core/enums';
import { AuthenticationService } from 'core/services/authentication.service';
import { NotificationService } from 'core/services/infrastructure/notification.service';
import {
  PartnerResource,
  PartnerResourceFilter,
  PartnersService,
} from 'core/services/partners.service';
import { Resource, ResourcesService } from 'core/services/resources.service';
import { BehaviorSubject, Observable, delay, switchMap, tap } from 'rxjs';
import { ApiResponse } from 'shared/models/api-response';
import { Router } from '@angular/router';

@Component({
  selector: 'msep-partner-resources',
  templateUrl: './partner-resources.component.html',
  styleUrls: ['./partner-resources.component.scss'],
  standalone: false,
})
export class PartnerResourcesComponent implements OnInit {
  @ViewChild('paginator', { static: false }) paginator:
    | MatPaginator
    | undefined;

  categories$ = this.resourceService.getResourceCategories();
  downloadProgress: number = 0;
  fileType = 'application/pdf';
  form!: UntypedFormGroup;
  ghostLoaderType = GhostLoaderType.PartnerLogo;
  isUserManualDownload: boolean = false;
  isVhfUserManualDownload: boolean = false;
  isLoading = false;
  pageSize = 10;
  partnerResources: PartnerResource[] | undefined;
  partnerResourcesAction$!: Observable<PartnerResourceFilter>;
  partnerResources$!: Observable<ApiResponse<PartnerResource[]>>;
  resultTotal = 0;
  isTestEnvironment = false;
  userManual = PartnerResourceType.UserManual;
  vhfUserManual = PartnerResourceType.VhfUserManual;

  private readonly defaultFilter = {
    skip: 0,
    take: this.pageSize,
  } as PartnerResourceFilter;
  private readonly partnerResourcesSubject =
    new BehaviorSubject<PartnerResourceFilter>(this.defaultFilter);

  constructor(
    private readonly authenticationService: AuthenticationService,
    private readonly dialog: MatDialog,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly notificationService: NotificationService,
    private readonly partnerResourceService: PartnerResourceService,
    private readonly partnersService: PartnersService,
    private readonly resourceService: ResourcesService,
    private readonly router: Router
  ) {}

  get isAuthenticated(): boolean {
    return this.authenticationService.isAuthenticated();
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      id: null,
      title: null,
      categoryId: null,
    });

    this.setPartnerResourcesRequestStream();
  }

  downloadUserManual(): void {
    const fileName = 'MsepUserManual.pdf';
    if (this.isVhfUserManualDownload || this.isUserManualDownload) {
      this.notificationService.emitFailure(
        'Please wait until the current download is complete before starting another.'
      );
      return;
    }

    this.isUserManualDownload = true;
    this.isVhfUserManualDownload = false;
    this.downloadFile(
      () => this.resourceService.getUserManualSize(),
      (blockIndex: number) => this.resourceService.getUserManual(blockIndex),
      fileName,
      'userManual',
      () => {
        const partnerResourceTrackingTypeId =
          PartnerResourceTrackingType.PartnerResource;
        this.partnerResourceService
          .logPartnerResourceTracking(
            partnerResourceTrackingTypeId,
            PartnerResourceType.UserManual
          )
          .subscribe();
      }
    );
  }

  downloadVhfUserManual(): void {
    const fileName = 'VHFUserManual.pdf';
    if (this.isUserManualDownload || this.isVhfUserManualDownload) {
      this.notificationService.emitFailure(
        'Please wait until the current download is complete before starting another.'
      );
      return;
    }

    this.isVhfUserManualDownload = true;
    this.isUserManualDownload = false;
    this.downloadFile(
      () => this.resourceService.getVhfUserManualSize(),
      (blockIndex: number) => this.resourceService.getVhfUserManual(blockIndex),
      fileName,
      'vhfUserManual',
      () => {
        const partnerResourceTrackingTypeId =
          PartnerResourceTrackingType.PartnerResource;
        this.partnerResourceService
          .logPartnerResourceTracking(
            partnerResourceTrackingTypeId,
            PartnerResourceType.VhfUserManual
          )
          .subscribe();
      }
    );
  }

  onResourceCardClick(partnerResource: PartnerResource): void {
    const isExternalLink = partnerResource?.url?.startsWith('https');
    const partnerResourceTrackingTypeId =
      PartnerResourceTrackingType.PartnerResource;
    if (isExternalLink) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          title: 'Leaving the Military Spouse Employment Partnership portal',
          message:
            'You are about to leave the site. The appearance of hyperlinks does not constitute endorsement by the Defense Department of non-U.S. government sites or the information, products, or services contained therein. Other than for authorized activities such as military exchanges and MWR sites, the United States Defense Department does not exercise any editorial control over the information you may find at these locations. Such links are provided consistent with the stated purpose of this website.',
          buttonYes: 'Ok',
          buttonNo: 'Cancel',
          linkUrl: partnerResource.url,
          partnerResourceId: partnerResource.id,
        },
      });

      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          if (this.isAuthenticated) {
            this.partnerResourceService
              .logPartnerResourceTracking(
                partnerResourceTrackingTypeId,
                partnerResource.id
              )
              .subscribe({
                next: () => {
                  window.open(partnerResource.url, '_system');
                },
                error: () => {
                  window.open(partnerResource.url, '_system');
                },
              });
          } else {
            window.open(partnerResource.url, '_system');
          }
        }
      });
    } else {
      if (this.isAuthenticated) {
        this.partnerResourceService
          .logPartnerResourceTracking(
            partnerResourceTrackingTypeId,
            partnerResource.id
          )
          .subscribe();
      }
      this.router.navigateByUrl(partnerResource?.url ?? '');
    }
  }

  private downloadFile(
    getSizeFn: () => Observable<number>,
    getBlockFn: (blockIndex: number) => Observable<Blob>,
    filename: string,
    type: 'userManual' | 'vhfUserManual',
    afterDownload?: () => void
  ): void {
    this.downloadProgress = 0;

    getSizeFn().subscribe({
      next: fileSize => {
        const blockSize = 500 * 1024;
        const fileData: Blob[] = [];

        this.downloadBlocks(
          0,
          fileSize,
          blockSize,
          fileData,
          getBlockFn,
          () => {
            if (!this.isTestEnvironment) {
              const fullFile = new Blob(fileData, { type: this.fileType });
              const downloadUrl = URL.createObjectURL(fullFile);

              const link = document.createElement('a');
              link.href = downloadUrl;
              link.download = filename;
              link.click();

              URL.revokeObjectURL(downloadUrl);
            }
            if (type === 'userManual') {
              this.isUserManualDownload = false;
            } else {
              this.isVhfUserManualDownload = false;
            }
            if (afterDownload) {
              afterDownload();
            }
          },
          type
        );
      },
      error: () => {
        if (type === 'userManual') {
          this.isUserManualDownload = false;
        } else {
          this.isVhfUserManualDownload = false;
        }
        this.notificationService.emitFailure('Failed to download user manual');
      },
    });
  }

  private downloadBlocks(
    currentBlockIndex: number,
    fileSize: number,
    blockSize: number,
    fileData: Blob[],
    getBlockFn: (blockIndex: number) => Observable<Blob>,
    onComplete: () => void,
    type: 'userManual' | 'vhfUserManual'
  ): void {
    if (currentBlockIndex * blockSize >= fileSize) {
      onComplete();
      return;
    }

    getBlockFn(currentBlockIndex).subscribe({
      next: blockData => {
        blockData.arrayBuffer().then(arrayBuffer => {
          const uint8Array = new Uint8Array(arrayBuffer);
          const blob = this.createBlobFromUint8Array(uint8Array);
          if (!blob) {
            this.handleDownloadError(type);
            return;
          }
          fileData.push(blob);
          this.downloadProgress = Math.round(
            ((currentBlockIndex * blockSize) / fileSize) * 100
          );
          this.downloadBlocks(
            currentBlockIndex + 1,
            fileSize,
            blockSize,
            fileData,
            getBlockFn,
            onComplete,
            type
          );
        });
      },
      error: () => {
        this.handleDownloadError(type);
      },
    });
  }

  private createBlobFromUint8Array(uint8Array: Uint8Array): Blob | null {
    const isBase64 = this.isBase64Encoded(uint8Array);
    if (isBase64) {
      const base64Blob = this.handleBase64Encoding(uint8Array);
      if (!base64Blob) {
        return null;
      }
      return base64Blob;
    }
    return new Blob([uint8Array], { type: this.fileType });
  }

  private handleDownloadError(type: 'userManual' | 'vhfUserManual'): void {
    if (type === 'userManual') {
      this.isUserManualDownload = false;
    } else {
      this.isVhfUserManualDownload = false;
    }
    this.notificationService.emitFailure('Error downloading user manual');
  }

  onCategoryChanged(event: MatSelectChange): void {
    this.form.patchValue({
      categoryId: event.value,
    });
    this.search();
  }

  onPagingChange(event: { skip: number; take: number }): void {
    const filter = this.form.value as PartnerResourceFilter;
    this.pageSize = event.take;
    filter.take = event.take;
    filter.skip = event.skip;
    this.partnerResourcesSubject.next(filter);
  }

  onReset(): void {
    this.form.reset();
    this.paginator?.firstPage();
    this.pageSize = this.defaultFilter.take;
    this.partnerResourcesSubject.next(this.defaultFilter);
  }

  onTitleSelected(title: Resource): void {
    this.form.patchValue({ id: title.id });
    this.search();
  }

  private isBase64Encoded(uint8Array: Uint8Array): boolean {
    const base64Pattern = /^[A-Za-z0-9+/=]+$/;
    const decodedString = new TextDecoder().decode(uint8Array);

    return base64Pattern.test(decodedString.trim());
  }

  private handleBase64Encoding(uint8Array: Uint8Array): Blob | null {
    try {
      const arrayBuffer = uint8Array.buffer;
      const base64String = new TextDecoder().decode(arrayBuffer);

      const binaryString = atob(base64String);
      const byteArray = new Uint8Array(binaryString.length);

      for (let i = 0; i < binaryString.length; i++) {
        byteArray[i] = binaryString.charCodeAt(i);
      }

      return new Blob([byteArray], { type: this.fileType });
    } catch (error) {
      this.notificationService.emitFailure(
        'Error decoding Base64 data: ' + error
      );
      return null;
    }
  }

  private search(): void {
    const filter = this.form.value as PartnerResourceFilter;
    filter.take = this.paginator?.pageSize ?? this.pageSize;
    filter.skip = 0;
    if (this.paginator !== undefined) {
      this.paginator.firstPage();
    }
    this.partnerResourcesSubject.next(filter);
  }

  private setPartnerResourcesRequestStream(): void {
    this.partnerResourcesAction$ = this.partnerResourcesSubject.asObservable();
    this.partnerResources$ = this.partnerResourcesAction$.pipe(
      delay(0),
      tap(() => (this.isLoading = true)),
      switchMap((filters: PartnerResourceFilter) =>
        this.partnersService.getPartnerResources(filters)
      ),
      tap(results => {
        this.resultTotal = results.total;
        this.partnerResources = results.data;
        this.isLoading = false;
      })
    );
  }
}
